
import { useState, useEffect } from 'react'
import {
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Button,
  Select,
  Grid,
  Spinner,
  Box,
  Image,
  IconButton,
  Heading,
  Divider,
  Card,
  CardBody,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Badge,
  Tooltip,
  Textarea,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { X, Globe, Clock, Languages, Image as ImageIcon, Info, Upload, CheckCircle2, Contrast } from 'lucide-react'
import { SettingsService, GeneralSettingsData } from '@/lib/services/settingsService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { timezones, dateFormats, timeFormats } from '@/lib/utils/dateTimeUtils'
import { useSiteSettings } from '@/hooks/useSiteSettings'

interface GeneralSettingsFormProps {
  onSuccess?: () => void
}

export function GeneralSettingsForm({ onSuccess }: GeneralSettingsFormProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<GeneralSettingsData | null>(null)
  const [logoPreviews, setLogoPreviews] = useState<{
    auth_logo?: string
    sidenav_logo?: string
  }>({})
  const [hasChanges, setHasChanges] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [dragActive, setDragActive] = useState<{ auth_logo: boolean; sidenav_logo: boolean }>({
    auth_logo: false,
    sidenav_logo: false,
  })
  const [previewBg, setPreviewBg] = useState<{ auth_logo: 'light' | 'dark'; sidenav_logo: 'light' | 'dark' }>({
    auth_logo: 'light',
    sidenav_logo: 'light',
  })
  const { showError, showSuccess } = useToastNotification()
  const { refresh: refreshSiteSettings } = useSiteSettings()

  useEffect(() => {
    loadSettings()
  }, [])

  // No cleanup needed for data URLs (they're self-contained strings)

  const loadSettings = async () => {
    try {
      setLoading(true)
      console.log('[GeneralSettingsForm] Loading settings...')
      const response = await SettingsService.getGeneralSettings()
      console.log('[GeneralSettingsForm] Settings loaded:', response)
      
      if (response.success && response.data) {
        // Ensure all required fields exist with proper defaults
        const loadedSettings: GeneralSettingsData = {
          site: {
            site_name: response.data.site?.site_name ?? '',
            site_description: response.data.site?.site_description ?? '',
            auth_logo: response.data.site?.auth_logo ?? null,
            sidenav_logo: response.data.site?.sidenav_logo ?? null,
          },
          date_time: {
            timezone: response.data.date_time?.timezone ?? 'UTC',
            date_format: response.data.date_time?.date_format ?? 'Y-m-d',
            time_format: response.data.date_time?.time_format ?? 'H:i:s',
          },
          language: {
            default_language: response.data.language?.default_language ?? 'en',
          },
        }
        setSettings(loadedSettings)
        setHasChanges(false)
        
        // Set logo previews from loaded settings
        // Backend now returns full URLs, so we can use them directly
        const buildLogoUrl = (logoPath: string | File | null | undefined): string | undefined => {
          // If it's a File object, return undefined (will be handled by handleLogoChange)
          if (logoPath instanceof File) return undefined
          
          // If it's null or undefined, return undefined to clear preview
          if (logoPath === null || logoPath === undefined) {
            console.log('buildLogoUrl: Null or undefined path', logoPath)
            return undefined
          }
          
          if (typeof logoPath !== 'string' || logoPath.trim() === '') {
            console.log('buildLogoUrl: Empty or invalid path', logoPath)
            return undefined
          }
          
          // Normalize escaped slashes from JSON (http:\/\/ becomes http://)
          const normalizedPath = logoPath.replace(/\\\//g, '/')
          
          // If it's already a full URL (from backend), return as is
          if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            console.log('buildLogoUrl: Full URL detected', normalizedPath)
            return normalizedPath
          }
          
          // If it's a blob URL or data URL (for new uploads), return as is
          if (normalizedPath.startsWith('blob:') || normalizedPath.startsWith('data:')) {
            return normalizedPath
          }
          
          // Fallback: Build URL from relative path (for backward compatibility)
          // This should rarely be needed now that backend returns full URLs
          const baseUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://127.0.0.1:8000'
          let cleanPath = normalizedPath.trim()
          
          // Remove leading slash if present
          if (cleanPath.startsWith('/')) {
            cleanPath = cleanPath.substring(1)
          }
          
          // If path starts with 'storage/', use it as is (add leading slash)
          if (cleanPath.startsWith('storage/')) {
            cleanPath = `/${cleanPath}`
          } else {
            cleanPath = `/storage/${cleanPath}`
          }
          
          const fullUrl = `${baseUrl}${cleanPath}`
          console.log('Building logo URL (fallback):', { original: logoPath, cleanPath, fullUrl })
          return fullUrl
        }
        
        const authLogoUrl = buildLogoUrl(loadedSettings.site.auth_logo)
        const sidenavLogoUrl = buildLogoUrl(loadedSettings.site.sidenav_logo)
        
        console.log('Setting logo previews:', {
          auth_logo: {
            original: loadedSettings.site.auth_logo,
            processed: authLogoUrl,
            type: typeof loadedSettings.site.auth_logo
          },
          sidenav_logo: {
            original: loadedSettings.site.sidenav_logo,
            processed: sidenavLogoUrl,
            type: typeof loadedSettings.site.sidenav_logo
          }
        })
        
        setLogoPreviews({
          auth_logo: authLogoUrl,
          sidenav_logo: sidenavLogoUrl,
        })
      } else {
        // Initialize with defaults if no data
        const defaultSettings: GeneralSettingsData = {
          site: {
            site_name: '',
            site_description: '',
            auth_logo: '',
            sidenav_logo: '',
          },
          date_time: {
            timezone: 'UTC',
            date_format: 'Y-m-d',
            time_format: 'H:i:s',
          },
          language: {
            default_language: 'en',
          },
        }
        setSettings(defaultSettings)
        setHasChanges(false)
      }
    } catch (err: any) {
      // Initialize with defaults on error
      console.error('[GeneralSettingsForm] Error loading settings:', err)
      console.error('[GeneralSettingsForm] Error response:', err.response)
      setSettings({
        site: {
          site_name: '',
          site_description: '',
          auth_logo: '',
          sidenav_logo: '',
        },
        date_time: {
          timezone: 'UTC',
          date_format: 'Y-m-d',
          time_format: 'H:i:s',
        },
        language: {
          default_language: 'en',
        },
      })
      setHasChanges(false)
      showError(err.message || 'Failed to load general settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!settings) return
    
    try {
      setSaving(true)
      setErrors({}) // Clear previous errors
      // Scroll to top before saving
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
      const response = await SettingsService.updateGeneralSettings(settings)
      if (response.success) {
        showSuccess('Success', response.message || 'General settings updated successfully')
        // Reload settings to ensure form displays latest data (including updated logo URLs)
        await loadSettings()
        // Refresh site settings cache so all components using useSiteSettings get updated values
        refreshSiteSettings()
        setHasChanges(false)
        setLastSaved(new Date())
        setErrors({}) // Clear errors on success
        // Scroll to top after successful save
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
        onSuccess?.()
      } else {
        throw new Error(response.message || 'Failed to update general settings')
      }
    } catch (err: any) {
      // Handle validation errors
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors
        setErrors(validationErrors)
        
        // Show general error message
        const errorMessages: string[] = []
        Object.entries(validationErrors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            errorMessages.push(...messages)
          }
        })
        
        if (errorMessages.length > 0) {
          showError(errorMessages.join(', '))
        } else {
          showError(err.response.data.message || 'Validation failed')
        }
      } else {
        // Handle other errors
        setErrors({})
        showError(err.message || 'Failed to update general settings')
      }
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (section: keyof GeneralSettingsData, key: string, value: any) => {
    if (!settings) return
    
    setHasChanges(true)
    setSettings((prev) => {
      if (!prev) return null
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value,
        },
      }
    })
  }

  const handleLogoChange = (logoType: 'auth_logo' | 'sidenav_logo', file: File | null) => {
    if (!settings) return
    
    // Clear error for this field when user selects a new file
    const errorKey = `site.${logoType}`
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[errorKey]
        return newErrors
      })
    }
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Please select a valid image file')
        return
      }
      
      // Validate file size (max 2MB)
      const maxSize = 2 * 1024 * 1024 // 2MB in bytes
      if (file.size > maxSize) {
        showError('Image file size must be less than 2MB')
        return
      }
      
      // Clear old preview (no need to revoke data URLs)
      // Blob URLs would need revoking, but data URLs are self-contained
      
      // Create preview URL using FileReader for better compatibility
      try {
        const reader = new FileReader()
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string
          if (dataUrl) {
            console.log('Created data URL for preview:', { 
              logoType, 
              fileName: file.name, 
              fileType: file.type,
              fileSize: file.size,
              dataUrlLength: dataUrl.length
            })
            
            // Update preview with data URL
            setLogoPreviews((prev) => ({
              ...prev,
              [logoType]: dataUrl,
            }))
          }
        }
        reader.onerror = (error) => {
          console.error('FileReader error:', error)
          showError('Failed to read image file. Please try again.')
        }
        
        // Read file as data URL
        reader.readAsDataURL(file)
        
        // Update settings with File object
        updateSetting('site', logoType, file)
      } catch (error) {
        console.error('Failed to create image preview:', error)
        showError('Failed to create image preview. Please try again.')
      }
    } else {
      // Clear logo preview
      setLogoPreviews((prev) => {
        const newPreviews = { ...prev }
        delete newPreviews[logoType]
        return newPreviews
      })
      
      // Set to null to clear on backend
      updateSetting('site', logoType, null)
    }
  }

  const handleLogoRemove = (logoType: 'auth_logo' | 'sidenav_logo') => {
    handleLogoChange(logoType, null)
  }

  // Date/time utilities are imported from @/lib/utils/dateTimeUtils

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' },
    { value: 'zh', label: 'Chinese' },
    { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'ar', label: 'Arabic' },
    { value: 'hi', label: 'Hindi' },
    { value: 'nl', label: 'Dutch' },
    { value: 'pl', label: 'Polish' },
    { value: 'tr', label: 'Turkish' },
    { value: 'vi', label: 'Vietnamese' },
    { value: 'th', label: 'Thai' },
    { value: 'id', label: 'Indonesian' },
    { value: 'ms', label: 'Malay' },
    { value: 'cs', label: 'Czech' },
    { value: 'sv', label: 'Swedish' },
    { value: 'da', label: 'Danish' },
    { value: 'fi', label: 'Finnish' },
    { value: 'no', label: 'Norwegian' },
    { value: 'he', label: 'Hebrew' },
    { value: 'uk', label: 'Ukrainian' },
    { value: 'ro', label: 'Romanian' },
    { value: 'hu', label: 'Hungarian' },
    { value: 'el', label: 'Greek' },
    { value: 'bg', label: 'Bulgarian' },
  ]

  const colors = useThemeColors()
  const cardBg = colors.cardBg
  const borderColor = colors.border
  const sectionBg = colors.sectionBg
  const uploadHoverBg = colors.uploadHover
  const uploadIconColor = colors.uploadIcon

  if (loading || !settings) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading general settings...</Text>
      </VStack>
    )
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Display general validation errors */}
      {Object.keys(errors).length > 0 && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            <Text fontWeight="semibold" mb={2}>Please fix the following errors:</Text>
            <VStack align="stretch" spacing={1}>
              {Object.entries(errors).map(([field, messages]) => (
                <Box key={field}>
                  {Array.isArray(messages) && messages.map((msg, idx) => (
                    <Text key={idx} fontSize="sm">• {msg}</Text>
                  ))}
                </Box>
              ))}
            </VStack>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Site Information Section */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="blue.100" borderRadius="md" color="blue.600">
              <Globe size={20} />
            </Box>
            <Heading size="sm" fontWeight="semibold">
              Site Information
            </Heading>
          </HStack>
          
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Site Name
              </FormLabel>
              <FormControl>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" color={colors.uploadIcon}>
                    <Globe size={16} />
                  </InputLeftElement>
                  <Input
                    value={settings?.site?.site_name || ''}
                    onChange={(e) => updateSetting('site', 'site_name', e.target.value)}
                    placeholder="Enter site name"
                    pl={10}
                  />
                </InputGroup>
                <FormHelperText>The name of your application</FormHelperText>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Site Description
              </FormLabel>
              <FormControl>
                <Textarea
                  value={settings?.site?.site_description || ''}
                  onChange={(e) => updateSetting('site', 'site_description', e.target.value)}
                  placeholder="Enter site description"
                  rows={3}
                  resize="vertical"
                />
                <FormHelperText>A brief description of your application</FormHelperText>
              </FormControl>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Logo Settings Section */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="purple.100" borderRadius="md" color="purple.600">
              <ImageIcon size={20} />
            </Box>
            <Heading size="sm" fontWeight="semibold">
              Logo Settings
            </Heading>
          </HStack>
          
          <VStack spacing={6} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Authentication Logo
              </FormLabel>
              <FormControl isInvalid={!!errors['site.auth_logo']}>
                <VStack align="stretch" spacing={3}>
                  {logoPreviews.auth_logo && (
                    <Box 
                      position="relative" 
                      w="full" 
                      maxW="300px" 
                      minH="120px" 
                      border="2px dashed" 
                      borderColor={borderColor}
                      borderRadius="lg" 
                      p={4} 
                      bg={sectionBg}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backgroundImage={
                        previewBg.auth_logo === 'light'
                          ? "repeating-linear-gradient(45deg, #f0f0f0 0px, #f0f0f0 10px, #ffffff 10px, #ffffff 20px)"
                          : "repeating-linear-gradient(45deg, #666666 0px, #666666 10px, #333333 10px, #333333 20px)"
                      }
                      backgroundSize="20px 20px"
                    >
                      <img
                        key={logoPreviews.auth_logo} // Force re-render when URL changes
                        src={logoPreviews.auth_logo}
                        alt="Authentication logo preview"
                        style={{
                          maxHeight: '120px',
                          maxWidth: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          backgroundColor: 'transparent',
                        }}
                        onLoad={() => {
                          console.log('✅ Image loaded successfully:', logoPreviews.auth_logo)
                        }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                          const target = e.currentTarget
                          console.error('❌ Failed to load logo image:', {
                            src: logoPreviews.auth_logo,
                            attemptedSrc: target.src,
                            error: e,
                            naturalWidth: target.naturalWidth,
                            naturalHeight: target.naturalHeight
                          })
                        }}
                      />
                      <HStack
                        position="absolute"
                        top={2}
                        right={2}
                        spacing={1}
                        zIndex={1}
                      >
                        <Tooltip
                          label={previewBg.auth_logo === 'light' ? 'Switch to dark background' : 'Switch to light background'}
                          placement="top"
                          hasArrow
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                        >
                          <IconButton
                            aria-label="Toggle background"
                            icon={<Contrast size={16} />}
                            size="sm"
                            onClick={() => setPreviewBg((prev) => ({
                              ...prev,
                              auth_logo: prev.auth_logo === 'light' ? 'dark' : 'light'
                            }))}
                            colorScheme={previewBg.auth_logo === 'light' ? 'blue' : 'purple'}
                            variant="solid"
                            bg={previewBg.auth_logo === 'light' ? 'blue.500' : 'purple.500'}
                            _hover={{
                              bg: previewBg.auth_logo === 'light' ? 'blue.600' : 'purple.600',
                              transform: 'scale(1.1)'
                            }}
                            boxShadow="md"
                          />
                        </Tooltip>
                        <IconButton
                          aria-label="Remove logo"
                          icon={<X size={16} />}
                          size="sm"
                          onClick={() => handleLogoRemove('auth_logo')}
                          colorScheme="red"
                          variant="solid"
                        />
                      </HStack>
                    </Box>
                  )}
                  <Box
                    border="2px dashed"
                    borderColor={dragActive.auth_logo ? 'blue.400' : borderColor}
                    borderRadius="md"
                    p={4}
                    textAlign="center"
                    _hover={{ borderColor: 'blue.400', bg: uploadHoverBg }}
                    transition="all 0.2s"
                    cursor="pointer"
                    position="relative"
                    bg={dragActive.auth_logo ? uploadHoverBg : undefined}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDragActive((prev) => ({ ...prev, auth_logo: true }))
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDragActive((prev) => ({ ...prev, auth_logo: false }))
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDragActive((prev) => ({ ...prev, auth_logo: false }))
                      const file = e.dataTransfer.files?.[0] || null
                      if (file) {
                        handleLogoChange('auth_logo', file)
                      }
                    }}
                  >
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null
                        handleLogoChange('auth_logo', file)
                        // Reset input so same file can be selected again
                        e.target.value = ''
                      }}
                      size="sm"
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      opacity={0}
                      cursor="pointer"
                    />
                    <VStack spacing={2}>
                      <Upload size={24} color={uploadIconColor} />
                      <Text fontSize="sm" color="gray.500">
                        Click to upload or drag and drop
                      </Text>
                      <Text fontSize="xs" color={colors.textMuted}>
                        PNG, JPG, GIF, SVG up to 2MB
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
                {errors['site.auth_logo'] ? (
                  <FormErrorMessage mt={2}>
                    {errors['site.auth_logo'].map((msg, idx) => (
                      <Text key={idx} fontSize="sm">{msg}</Text>
                    ))}
                  </FormErrorMessage>
                ) : (
                  <FormHelperText mt={2}>
                    Upload logo for login and forgot password pages (JPEG, PNG, JPG, GIF, SVG, max 2MB)
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Divider />

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Sidebar Navigation Logo
              </FormLabel>
              <FormControl isInvalid={!!errors['site.sidenav_logo']}>
                <VStack align="stretch" spacing={3}>
                  {logoPreviews.sidenav_logo && (
                    <Box 
                      position="relative" 
                      w="full" 
                      maxW="300px" 
                      minH="120px" 
                      border="2px dashed" 
                      borderColor={borderColor}
                      borderRadius="lg" 
                      p={4} 
                      bg={sectionBg}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      backgroundImage={
                        previewBg.sidenav_logo === 'light'
                          ? "repeating-linear-gradient(45deg, #f0f0f0 0px, #f0f0f0 10px, #ffffff 10px, #ffffff 20px)"
                          : "repeating-linear-gradient(45deg, #666666 0px, #666666 10px, #333333 10px, #333333 20px)"
                      }
                      backgroundSize="20px 20px"
                    >
                      <img
                        key={logoPreviews.sidenav_logo} // Force re-render when URL changes
                        src={logoPreviews.sidenav_logo}
                        alt="Sidenav logo preview"
                        style={{
                          maxHeight: '120px',
                          maxWidth: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                          backgroundColor: 'transparent',
                        }}
                        onLoad={() => {
                          console.log('✅ Image loaded successfully:', logoPreviews.sidenav_logo)
                        }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                          const target = e.currentTarget
                          console.error('❌ Failed to load logo image:', {
                            src: logoPreviews.sidenav_logo,
                            attemptedSrc: target.src,
                            error: e,
                            naturalWidth: target.naturalWidth,
                            naturalHeight: target.naturalHeight
                          })
                        }}
                      />
                      <HStack
                        position="absolute"
                        top={2}
                        right={2}
                        spacing={1}
                        zIndex={1}
                      >
                        <Tooltip
                          label={previewBg.sidenav_logo === 'light' ? 'Switch to dark background' : 'Switch to light background'}
                          placement="top"
                          hasArrow
                          bg="gray.800"
                          color="white"
                          fontSize="sm"
                        >
                          <IconButton
                            aria-label="Toggle background"
                            icon={<Contrast size={16} />}
                            size="sm"
                            onClick={() => setPreviewBg((prev) => ({
                              ...prev,
                              sidenav_logo: prev.sidenav_logo === 'light' ? 'dark' : 'light'
                            }))}
                            colorScheme={previewBg.sidenav_logo === 'light' ? 'blue' : 'purple'}
                            variant="solid"
                            bg={previewBg.sidenav_logo === 'light' ? 'blue.500' : 'purple.500'}
                            _hover={{
                              bg: previewBg.sidenav_logo === 'light' ? 'blue.600' : 'purple.600',
                              transform: 'scale(1.1)'
                            }}
                            boxShadow="md"
                          />
                        </Tooltip>
                        <IconButton
                          aria-label="Remove logo"
                          icon={<X size={16} />}
                          size="sm"
                          onClick={() => handleLogoRemove('sidenav_logo')}
                          colorScheme="red"
                          variant="solid"
                        />
                      </HStack>
                    </Box>
                  )}
                  <Box
                    border="2px dashed"
                    borderColor={dragActive.sidenav_logo ? 'blue.400' : borderColor}
                    borderRadius="md"
                    p={4}
                    textAlign="center"
                    _hover={{ borderColor: 'blue.400', bg: uploadHoverBg }}
                    transition="all 0.2s"
                    cursor="pointer"
                    position="relative"
                    bg={dragActive.sidenav_logo ? uploadHoverBg : undefined}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDragActive((prev) => ({ ...prev, sidenav_logo: true }))
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDragActive((prev) => ({ ...prev, sidenav_logo: false }))
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDragActive((prev) => ({ ...prev, sidenav_logo: false }))
                      const file = e.dataTransfer.files?.[0] || null
                      if (file) {
                        handleLogoChange('sidenav_logo', file)
                      }
                    }}
                  >
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null
                        handleLogoChange('sidenav_logo', file)
                        // Reset input so same file can be selected again
                        e.target.value = ''
                      }}
                      size="sm"
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      opacity={0}
                      cursor="pointer"
                    />
                    <VStack spacing={2}>
                      <Upload size={24} color={uploadIconColor} />
                      <Text fontSize="sm" color="gray.500">
                        Click to upload or drag and drop
                      </Text>
                      <Text fontSize="xs" color={colors.textMuted}>
                        PNG, JPG, GIF, SVG up to 2MB
                      </Text>
                    </VStack>
                  </Box>
                </VStack>
                {errors['site.sidenav_logo'] ? (
                  <FormErrorMessage mt={2}>
                    {errors['site.sidenav_logo'].map((msg, idx) => (
                      <Text key={idx} fontSize="sm">{msg}</Text>
                    ))}
                  </FormErrorMessage>
                ) : (
                  <FormHelperText mt={2}>
                    Upload logo for sidebar navigation (JPEG, PNG, JPG, GIF, SVG, max 2MB)
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Date & Time Settings Section */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="orange.100" borderRadius="md" color="orange.600">
              <Clock size={20} />
            </Box>
            <Heading size="sm" fontWeight="semibold">
              Date & Time Settings
            </Heading>
          </HStack>
          
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Timezone
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.date_time?.timezone || 'UTC'}
                    onChange={(e) => updateSetting('date_time', 'timezone', e.target.value)}
                    flex="1"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </Select>
                  <Badge colorScheme="blue" fontSize="sm" px={2} py={1}>
                    {settings?.date_time?.timezone || 'UTC'}
                  </Badge>
                </HStack>
                <FormHelperText>Default timezone for the application</FormHelperText>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Date Format
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.date_time?.date_format || 'Y-m-d'}
                    onChange={(e) => updateSetting('date_time', 'date_format', e.target.value)}
                    flex="1"
                  >
                    {dateFormats.map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </Select>
                  <Tooltip label="Preview of current date format">
                    <Badge colorScheme="purple" fontSize="sm" px={2} py={1} cursor="help">
                      {(() => {
                        const now = new Date()
                        const format = settings?.date_time?.date_format || 'Y-m-d'
                        if (format === 'Y-m-d') return now.toISOString().split('T')[0]
                        if (format === 'm/d/Y') return `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getDate()).padStart(2, '0')}/${now.getFullYear()}`
                        if (format === 'd/m/Y') return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`
                        return now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                      })()}
                    </Badge>
                  </Tooltip>
                </HStack>
                <FormHelperText>How dates are displayed throughout the application</FormHelperText>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Time Format
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.date_time?.time_format || 'H:i:s'}
                    onChange={(e) => updateSetting('date_time', 'time_format', e.target.value)}
                    flex="1"
                  >
                    {timeFormats.map((format) => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </Select>
                  <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
                    {new Date().toLocaleTimeString('en-US', {
                      hour12: settings?.date_time?.time_format?.includes('A') || false
                    })}
                  </Badge>
                </HStack>
                <FormHelperText>How times are displayed throughout the application</FormHelperText>
              </FormControl>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Language Settings Section */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={3} mb={4}>
            <Box p={2} bg="green.100" borderRadius="md" color="green.600">
              <Languages size={20} />
            </Box>
            <Heading size="sm" fontWeight="semibold">
              Language Settings
            </Heading>
          </HStack>
          
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
              <FormLabel pt={2} fontWeight="medium">
                Default Language
              </FormLabel>
              <FormControl>
                <HStack spacing={2}>
                  <Select
                    value={settings?.language?.default_language || 'en'}
                    onChange={(e) => updateSetting('language', 'default_language', e.target.value)}
                    flex="1"
                  >
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </Select>
                  <Badge colorScheme="green" fontSize="sm" px={2} py={1}>
                    {languages.find(l => l.value === settings?.language?.default_language)?.label || 'English'}
                  </Badge>
                </HStack>
                <FormHelperText>Default language for the application interface</FormHelperText>
              </FormControl>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody>
          <HStack spacing={4} justify="space-between" align="center">
            <HStack spacing={2}>
              {hasChanges && (
                <Badge colorScheme="orange" fontSize="xs">
                  Unsaved changes
                </Badge>
              )}
              {lastSaved && !hasChanges && (
                <HStack spacing={1} color="green.500">
                  <CheckCircle2 size={14} />
                  <Text fontSize="xs" color="green.500">
                    Saved {lastSaved.toLocaleTimeString()}
                  </Text>
                </HStack>
              )}
            </HStack>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
              size="md"
              px={8}
              isDisabled={!hasChanges && !saving}
            >
              Save Settings
            </Button>
          </HStack>
        </CardBody>
      </Card>
    </VStack>
  )
}

